/**
 * Bona Pisca - Error Boundary Component
 * Comprehensive error handling for mobile fishing app with graceful degradation
 */

import React, { Component, ErrorInfo, ReactNode } from 'react';
import i18next from 'i18next';
import { useTranslation } from 'react-i18next';
import { Material3Button } from './Material3Button';
import { Material3Card } from './Material3Card';

interface ErrorBoundaryState {
  hasError: boolean;
  error: Error | null;
  errorInfo: ErrorInfo | null;
  errorId: string;
}

interface ErrorBoundaryProps {
  children: ReactNode;
  fallback?: ReactNode;
  onError?: (error: Error, errorInfo: ErrorInfo) => void;
  showDetails?: boolean;
  context?: string; // e.g., 'species-list', 'catch-form', 'weather-widget'
}

class ErrorBoundary extends Component<ErrorBoundaryProps, ErrorBoundaryState> {
  constructor(props: ErrorBoundaryProps) {
    super(props);
    
    this.state = {
      hasError: false,
      error: null,
      errorInfo: null,
      errorId: ''
    };
  }

  static getDerivedStateFromError(error: Error): Partial<ErrorBoundaryState> {
    // Generate unique error ID for tracking
    const errorId = `err-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
    
    return {
      hasError: true,
      error,
      errorId
    };
  }

  componentDidCatch(error: Error, errorInfo: ErrorInfo) {
    // Update state with error details
    this.setState({
      error,
      errorInfo
    });

    // Log error for debugging (in production, this would go to error tracking service)
    console.error('[ErrorBoundary] Caught error:', {
      error,
      errorInfo,
      context: this.props.context,
      errorId: this.state.errorId,
      userAgent: navigator.userAgent,
      url: window.location.href,
      timestamp: new Date().toISOString()
    });

    // Call custom error handler if provided
    if (this.props.onError) {
      this.props.onError(error, errorInfo);
    }

    // In production, send to error tracking service
    if (process.env.NODE_ENV === 'production') {
      // This would integrate with services like Sentry, Bugsnag, etc.
      // For now, just log to help with debugging
      console.warn('[ErrorBoundary] In production, this error would be sent to error tracking service');
    }
  }

  handleRetry = () => {
    this.setState({
      hasError: false,
      error: null,
      errorInfo: null,
      errorId: ''
    });
  };

  handleReload = () => {
    window.location.reload();
  };

  t = (key: string, options?: Record<string, unknown>) => i18next.t(key, { ns: 'common', ...options });

  render() {
    if (this.state.hasError) {
      // Use custom fallback if provided
      if (this.props.fallback) {
        return this.props.fallback;
      }

      // Default Mediterranean-styled error UI
      return (
        <Material3Card variant="elevated" className="min-h-[200px] flex items-center justify-center p-6">
          <div className="text-center space-y-4 max-w-md">
            <div className="w-16 h-16 mx-auto bg-error-container rounded-full flex items-center justify-center">
              <span className="text-2xl" role="img" aria-label={this.t('ui.errorBoundary.iconLabel')}>⚠️</span>
            </div>
            
            <div className="space-y-2">
              <h3 className="text-lg font-semibold text-on-surface">
                {this.t('ui.errorBoundary.title')}
              </h3>
              <p className="text-sm text-on-surface-variant">
                {this.props.context 
                  ? this.t('ui.errorBoundary.contextBody', { context: this.props.context })
                  : this.t('ui.errorBoundary.body')
                }
              </p>
            </div>

            <div className="flex flex-col space-y-2">
              <Material3Button
                variant="filled"
                size="sm"
                onClick={this.handleRetry}
                fullWidth
              >
                {this.t('actions.tryAgain')}
              </Material3Button>
              
              <Material3Button
                variant="outlined"
                size="sm"
                onClick={this.handleReload}
                fullWidth
              >
                {this.t('ui.errorBoundary.reloadApp')}
              </Material3Button>
            </div>

            {/* Error details for development */}
            {(process.env.NODE_ENV === 'development' || this.props.showDetails) && this.state.error && (
              <details className="text-left mt-4 p-3 bg-stone-50 rounded-lg text-xs">
                <summary className="cursor-pointer font-medium text-stone-700 mb-2">
                  {this.t('ui.errorBoundary.technicalDetails', { errorId: this.state.errorId })}
                </summary>
                <div className="space-y-2">
                  <div>
                    <strong>{this.t('ui.errorBoundary.errorLabel')}:</strong> {this.state.error.message}
                  </div>
                  <div>
                    <strong>{this.t('ui.errorBoundary.stackLabel')}:</strong>
                    <pre className="whitespace-pre-wrap mt-1 text-stone-600">
                      {this.state.error.stack}
                    </pre>
                  </div>
                  {this.state.errorInfo && (
                    <div>
                      <strong>{this.t('ui.errorBoundary.componentStackLabel')}:</strong>
                      <pre className="whitespace-pre-wrap mt-1 text-stone-600">
                        {this.state.errorInfo.componentStack}
                      </pre>
                    </div>
                  )}
                </div>
              </details>
            )}
          </div>
        </Material3Card>
      );
    }

    return this.props.children;
  }
}

// Higher-order component for wrapping components with error boundaries
export function withErrorBoundary<P extends object>(
  WrappedComponent: React.ComponentType<P>,
  errorBoundaryConfig?: Omit<ErrorBoundaryProps, 'children'>
) {
  const WithErrorBoundaryComponent = (props: P) => (
    <ErrorBoundary {...errorBoundaryConfig}>
      <WrappedComponent {...props} />
    </ErrorBoundary>
  );

  WithErrorBoundaryComponent.displayName = 
    `withErrorBoundary(${WrappedComponent.displayName || WrappedComponent.name})`;

  return WithErrorBoundaryComponent;
}

// Specialized error boundaries for different Mediterranean fishing contexts

interface AsyncErrorBoundaryProps extends ErrorBoundaryProps {
  isLoading?: boolean;
  error?: Error | null;
  resetError?: () => void;
}

export const AsyncErrorBoundary: React.FC<AsyncErrorBoundaryProps> = ({
  children,
  isLoading,
  error,
  resetError,
  ...props
}) => {
  const { t } = useTranslation('common');

  // Handle async errors passed as props
  if (error && !isLoading) {
    return (
      <div className="min-h-[200px] flex items-center justify-center p-6">
        <div className="text-center space-y-4">
          <div className="w-12 h-12 mx-auto bg-mediterranean-red-lighter rounded-full flex items-center justify-center">
            <span className="text-xl">⚠️</span>
          </div>
          <div className="space-y-2">
            <h3 className="font-semibold text-stone-900">{t('ui.loading.unableToLoad')}</h3>
            <p className="text-sm text-stone-600">
              {error.message || t('ui.errorBoundary.networkCheck')}
            </p>
          </div>
          {resetError && (
            <Material3Button variant="filled" size="sm" onClick={resetError}>
              {t('actions.tryAgain')}
            </Material3Button>
          )}
        </div>
      </div>
    );
  }

  return (
    <ErrorBoundary {...props}>
      {children}
    </ErrorBoundary>
  );
};

// Specialized error boundaries for key Mediterranean fishing features

export const SpeciesErrorBoundary: React.FC<{ children: ReactNode }> = ({ children }) => {
  const { t } = useTranslation('common');

  return (
    <ErrorBoundary
      context={t('ui.errorBoundary.contexts.speciesData')}
      fallback={
      <Material3Card variant="elevated" className="p-6 text-center space-y-3">
        <span className="text-4xl">🐟</span>
        <div>
          <h3 className="font-semibold text-on-surface">{t('ui.errorBoundary.speciesTitle')}</h3>
          <p className="text-sm text-on-surface-variant mt-1">
            {t('ui.errorBoundary.speciesBody')}
          </p>
        </div>
        <Material3Button size="sm" onClick={() => window.location.reload()}>
          {t('actions.refresh')}
        </Material3Button>
      </Material3Card>
      }
    >
      {children}
    </ErrorBoundary>
  );
};

export const WeatherErrorBoundary: React.FC<{ children: ReactNode }> = ({ children }) => {
  const { t } = useTranslation('common');

  return (
    <ErrorBoundary
      context={t('ui.errorBoundary.contexts.weatherData')}
      fallback={
      <Material3Card variant="elevated" className="p-6 text-center space-y-3">
        <span className="text-4xl">🌊</span>
        <div>
          <h3 className="font-semibold text-on-surface">{t('ui.errorBoundary.weatherTitle')}</h3>
          <p className="text-sm text-on-surface-variant mt-1">
            {t('ui.errorBoundary.weatherBody')}
          </p>
        </div>
      </Material3Card>
      }
    >
      {children}
    </ErrorBoundary>
  );
};

export const CatchErrorBoundary: React.FC<{ children: ReactNode }> = ({ children }) => {
  const { t } = useTranslation('common');

  return (
    <ErrorBoundary
      context={t('ui.errorBoundary.contexts.catchData')}
      fallback={
      <Material3Card variant="elevated" className="p-6 text-center space-y-3">
        <span className="text-4xl">🎣</span>
        <div>
          <h3 className="font-semibold text-on-surface">{t('ui.errorBoundary.logbookTitle')}</h3>
          <p className="text-sm text-on-surface-variant mt-1">
            {t('ui.errorBoundary.logbookBody')}
          </p>
        </div>
        <Material3Button size="sm" onClick={() => window.location.reload()}>
          {t('actions.refresh')}
        </Material3Button>
      </Material3Card>
      }
    >
      {children}
    </ErrorBoundary>
  );
};

// React error boundary hook for functional components
export function useErrorHandler() {
  return (error: Error, errorInfo?: ErrorInfo) => {
    console.error('[useErrorHandler] Manual error report:', {
      error,
      errorInfo,
      timestamp: new Date().toISOString(),
      url: window.location.href
    });

    // In production, this would send to error tracking
    if (process.env.NODE_ENV === 'production') {
      // Send to error tracking service
      console.warn('[useErrorHandler] Would send to error tracking service');
    }
  };
}

// Global error handler for unhandled promise rejections and errors
export function setupGlobalErrorHandling() {
  // Handle unhandled promise rejections
  window.addEventListener('unhandledrejection', (event) => {
    console.error('[GlobalErrorHandler] Unhandled promise rejection:', {
      reason: event.reason,
      promise: event.promise,
      timestamp: new Date().toISOString(),
      url: window.location.href
    });

    // Prevent default browser behavior
    event.preventDefault();

    // In production, send to error tracking
    if (process.env.NODE_ENV === 'production') {
      console.warn('[GlobalErrorHandler] Would send promise rejection to error tracking service');
    }
  });

  // Handle global JavaScript errors
  window.addEventListener('error', (event) => {
    console.error('[GlobalErrorHandler] Global error:', {
      message: event.message,
      filename: event.filename,
      lineno: event.lineno,
      colno: event.colno,
      error: event.error,
      timestamp: new Date().toISOString(),
      url: window.location.href
    });

    // In production, send to error tracking
    if (process.env.NODE_ENV === 'production') {
      console.warn('[GlobalErrorHandler] Would send global error to error tracking service');
    }
  });
}

export default ErrorBoundary;
